import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Image, FileText, Upload, Trash2, Search, X, Loader2,
  Package, ZoomIn, ChevronLeft, Download,
} from 'lucide-react';


/* ─── Image Lightbox ──────────────────────────────────────────────────────── */
const Lightbox = ({ src, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
    onClick={onClose}
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
    >
      <X size={28} />
    </button>
    <img
      src={src}
      alt="preview"
      className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl"
      onClick={(e) => e.stopPropagation()}
      onError={(e) => {
        // Fallback: probar .png -> .jpg -> .jpeg -> .gif si la extension falla
        const currentSrc = e.target.src;
        const baseUrl = currentSrc.replace(/\.[^./]+$/, '');
        const tried = e.target.dataset.tried ? e.target.dataset.tried.split(',') : [];
        const exts = ['.png', '.jpg', '.jpeg', '.gif'].filter(x => !tried.includes(x));
        if (exts.length === 0) return;
        const nextExt = exts[0];
        e.target.dataset.tried = [...tried, nextExt].join(',');
        e.target.src = `${baseUrl}${nextExt}`;
      }}
    />
  </div>
);

/* ─── Delete Confirm Modal ────────────────────────────────────────────────── */
const DeleteModal = ({ filename, type, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar eliminación</h3>
      <p className="text-sm text-gray-600 mb-4">
        ¿Eliminar {type === 'imagen' ? 'la imagen' : 'la ficha'}{' '}
        <span className="font-medium text-gray-800">{filename}</span>?
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
        >
          Eliminar
        </button>
      </div>
    </div>
  </div>
);

/* ========== CATALOGO PRODUCTOS ========== */
const CatalogoProductos = () => {
  const { token, API_URL, user } = useAuth();

  /* --- Download state --- */
  const [downloading, setDownloading] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const clientSearchTimeout = useRef(null);
  const clientDropdownRef = useRef(null);

  /* --- Search state --- */
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);

  /* --- Selected product --- */
  const [selectedProduct, setSelectedProduct] = useState(null);

  /* --- Catalog files --- */
  const [imagenes, setImagenes] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  /* --- Upload state --- */
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingFichas, setUploadingFichas] = useState(false);

  /* --- UI --- */
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { filename, type: 'imagen'|'ficha' }
  const [message, setMessage] = useState(null); // { text, type: 'success'|'error' }

  const imageInputRef = useRef(null);
  const fichaInputRef = useRef(null);

  /* --- Close dropdown on outside click --- */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* --- Auto-clear messages --- */
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  /* ─── Product search ────────────────────────────────────────────────────── */
  const searchProducts = useCallback(async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ search: term.trim(), page: 1, limit: 20 });
      const res = await fetch(`${API_URL}/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error buscando productos');
      const data = await res.json();
      setSearchResults(data.products || []);
      setShowDropdown(true);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [API_URL, token]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchProducts(val), 350);
  };

  /* ─── Select product & fetch files ──────────────────────────────────────── */
  const fetchFiles = useCallback(async (productId) => {
    setLoadingFiles(true);
    try {
      const res = await fetch(`${API_URL}/catalogo/producto/${productId}/archivos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar archivos');
      const data = await res.json();
      setImagenes(data.imagenes || []);
      setFichas(data.fichas || []);
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setLoadingFiles(false);
    }
  }, [API_URL, token]);

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setShowDropdown(false);
    setSearchTerm(product.nombre || '');
    fetchFiles(product.id);
  };

  const clearSelection = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setImagenes([]);
    setFichas([]);
  };

  /* ─── Catalog download ────────────────────────────────────────────────── */
  const isAdmin = user && (user.permisos === 1 || user.permisos === 6);

  const downloadCatalog = async (clientId) => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/catalogo/download/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al descargar catálogo');
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch ? filenameMatch[1] : 'Catalogo.xlsx';

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setMessage({ text: 'Catálogo descargado correctamente', type: 'success' });
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setDownloading(false);
      setShowClientSelector(false);
      setClientSearchTerm('');
      setClientSearchResults([]);
    }
  };

  const handleDownloadClick = () => {
    if (isAdmin) {
      setShowClientSelector(true);
    } else if (user?.cliente) {
      downloadCatalog(user.cliente);
    } else {
      setMessage({ text: 'No tiene un cliente asociado para descargar el catálogo', type: 'error' });
    }
  };

  const searchClients = async (term) => {
    if (!term.trim()) {
      setClientSearchResults([]);
      return;
    }
    setClientSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/clients/search?q=${encodeURIComponent(term.trim())}&limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error buscando clientes');
      const data = await res.json();
      setClientSearchResults(data.clients || []);
    } catch {
      setClientSearchResults([]);
    } finally {
      setClientSearchLoading(false);
    }
  };

  const handleClientSearchChange = (e) => {
    const val = e.target.value;
    setClientSearchTerm(val);
    if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);
    clientSearchTimeout.current = setTimeout(() => searchClients(val), 350);
  };

  /* Close client selector on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientSelector(false);
        setClientSearchTerm('');
        setClientSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ─── Upload images ─────────────────────────────────────────────────────── */
  const handleUploadImages = async (files) => {
    if (!selectedProduct || !files.length) return;
    setUploadingImages(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('imagenes', f));
      const res = await fetch(`${API_URL}/catalogo/producto/${selectedProduct.id}/imagen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al subir imágenes');
      }
      setMessage({ text: 'Imágenes subidas correctamente', type: 'success' });
      fetchFiles(selectedProduct.id);
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setUploadingImages(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  /* ─── Upload fichas ─────────────────────────────────────────────────────── */
  const handleUploadFichas = async (files) => {
    if (!selectedProduct || !files.length) return;
    setUploadingFichas(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append('fichas', f));
      const res = await fetch(`${API_URL}/catalogo/producto/${selectedProduct.id}/ficha`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al subir fichas');
      }
      setMessage({ text: 'Fichas subidas correctamente', type: 'success' });
      fetchFiles(selectedProduct.id);
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setUploadingFichas(false);
      if (fichaInputRef.current) fichaInputRef.current.value = '';
    }
  };

  /* ─── Delete file ───────────────────────────────────────────────────────── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { filename, type } = deleteTarget;
    const endpoint = type === 'imagen'
      ? `${API_URL}/catalogo/imagen/${encodeURIComponent(filename)}`
      : `${API_URL}/catalogo/ficha/${encodeURIComponent(filename)}`;
    try {
      const res = await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al eliminar');
      setMessage({ text: `${type === 'imagen' ? 'Imagen' : 'Ficha'} eliminada`, type: 'success' });
      setDeleteTarget(null);
      if (selectedProduct) fetchFiles(selectedProduct.id);
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
      setDeleteTarget(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="h-full flex flex-col overflow-hidden">

      {/* ─── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <Image className="w-6 h-6" style={{ color: '#0099FF' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Catálogo de Productos</h1>
              <p className="text-sm text-gray-500">Gestión de imágenes y fichas técnicas</p>
            </div>
          </div>

          {/* Download catalog button */}
          <div className="relative" ref={clientDropdownRef}>
            <button
              onClick={handleDownloadClick}
              disabled={downloading}
              style={{ backgroundColor: '#0099FF' }}
              className="text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50"
            >
              {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Descargar Catálogo
            </button>

            {/* Client selector modal for admin */}
            {showClientSelector && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Seleccionar cliente</h4>
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={handleClientSearchChange}
                  placeholder="Buscar cliente..."
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="mt-2 max-h-48 overflow-y-auto">
                  {clientSearchLoading ? (
                    <div className="p-3 text-center">
                      <Loader2 size={18} className="animate-spin mx-auto text-gray-400" />
                    </div>
                  ) : clientSearchResults.length === 0 && clientSearchTerm ? (
                    <div className="p-3 text-center text-sm text-gray-400">Sin resultados</div>
                  ) : (
                    clientSearchResults.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => downloadCatalog(c.id)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md transition-colors text-sm text-gray-700 truncate"
                      >
                        {c.razon_social || c.alias || `Cliente ${c.id}`}
                      </button>
                    ))
                  )}
                </div>
                <button
                  onClick={() => { setShowClientSelector(false); setClientSearchTerm(''); setClientSearchResults([]); }}
                  className="mt-2 w-full text-center text-xs text-gray-400 hover:text-gray-600"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Message banner */}
        {message && (
          <div
            className={`mt-4 p-3 rounded-md flex items-center justify-between text-sm ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="hover:opacity-70">
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {/* ─── Search bar ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          {selectedProduct && (
            <button
              onClick={clearSelection}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ChevronLeft size={16} /> Volver
            </button>
          )}
          <div className="relative flex-1 max-w-lg" ref={dropdownRef}>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => { if (searchResults.length && !selectedProduct) setShowDropdown(true); }}
              placeholder="Buscar producto por nombre o código..."
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => { setSearchTerm(''); setSearchResults([]); setShowDropdown(false); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}

            {/* Autocomplete dropdown */}
            {showDropdown && (
              <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                {searchLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 size={20} className="animate-spin mx-auto text-gray-400" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-400">Sin resultados</div>
                ) : (
                  searchResults.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => selectProduct(p)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-3"
                    >
                      <Package size={16} className="text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{p.nombre}</div>
                        <div className="text-xs text-gray-500">{p.raiz || p.codigo || '—'}</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Content ────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {!selectedProduct ? (
          /* ─── Empty state ──────────────────────────────────────────────────── */
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="p-4 rounded-full bg-gray-100 mb-4">
              <Search size={32} className="text-gray-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-700 mb-1">Buscar un producto</h2>
            <p className="text-sm text-gray-400 max-w-md">
              Usá la barra de búsqueda para encontrar un producto y gestionar sus imágenes y fichas técnicas.
            </p>
          </div>
        ) : (
          <>
            {/* ─── Product Info Card ─────────────────────────────────────────── */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 flex items-center gap-5">
              {/* Thumbnail */}
              {imagenes.length > 0 ? (
                <img
                  src={imagenes[0].url}
                  alt="thumb"
                  className="w-20 h-20 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Image size={28} className="text-gray-300" />
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">{selectedProduct.nombre}</h2>
                <p className="text-sm text-gray-500">
                  Código: <span className="font-medium text-gray-700">{selectedProduct.raiz || selectedProduct.codigo || '—'}</span>
                </p>
                <div className="flex gap-4 mt-1">
                  <span className="text-xs text-gray-400">{imagenes.length} imagen{imagenes.length !== 1 && 'es'}</span>
                  <span className="text-xs text-gray-400">{fichas.length} ficha{fichas.length !== 1 && 's'}</span>
                </div>
              </div>
            </div>

            {loadingFiles ? (
              <div className="flex justify-center py-12">
                <Loader2 size={28} className="animate-spin" style={{ color: '#0099FF' }} />
              </div>
            ) : (
              <>
                {/* ─── Images Section ──────────────────────────────────────────── */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                    <div className="flex items-center gap-2">
                      <Image size={18} className="text-blue-500" />
                      <h3 className="text-sm font-semibold text-gray-700">Imágenes</h3>
                    </div>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => imageInputRef.current?.click()}
                          disabled={uploadingImages}
                          style={{ backgroundColor: '#0099FF' }}
                          className="text-white px-3 py-1.5 rounded-md text-xs font-medium hover:opacity-90 transition-opacity flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {uploadingImages ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          Subir Imágenes
                        </button>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => handleUploadImages(e.target.files)}
                        />
                      </>
                    )}
                  </div>

                  <div className="p-5">
                    {imagenes.length === 0 ? (
                      <div className="text-center py-8">
                        <Image size={32} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">No hay imágenes cargadas</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {imagenes.map((img) => (
                          <div key={img.filename} className="group relative">
                            <div
                              className="aspect-square rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:shadow-md transition-shadow bg-gray-50"
                              onClick={() => setLightboxSrc(img.url)}
                            >
                              <img
                                src={img.url}
                                alt={img.filename}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                              </div>
                            </div>
                            {isAdmin && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteTarget({ filename: img.filename, type: 'imagen' }); }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                              >
                                <X size={12} />
                              </button>
                            )}
                            <p className="text-xs text-gray-400 mt-1 truncate" title={img.filename}>{img.filename}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ─── Fichas / PDFs Section ───────────────────────────────────── */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                    <div className="flex items-center gap-2">
                      <FileText size={18} className="text-green-500" />
                      <h3 className="text-sm font-semibold text-gray-700">Fichas Técnicas (PDF)</h3>
                    </div>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => fichaInputRef.current?.click()}
                          disabled={uploadingFichas}
                          className="text-white px-3 py-1.5 rounded-md text-xs font-medium hover:opacity-90 transition-opacity flex items-center gap-1.5 disabled:opacity-50"
                          style={{ backgroundColor: '#16a34a' }}
                        >
                          {uploadingFichas ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          Subir Fichas
                        </button>
                        <input
                          ref={fichaInputRef}
                          type="file"
                          accept="application/pdf"
                          multiple
                          className="hidden"
                          onChange={(e) => handleUploadFichas(e.target.files)}
                        />
                      </>
                    )}
                  </div>

                  <div className="p-5">
                    {fichas.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText size={32} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">No hay fichas técnicas cargadas</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {fichas.map((f) => (
                          <div
                            key={f.filename}
                            className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                          >
                            <a
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 min-w-0 flex-1 hover:text-blue-600 transition-colors"
                            >
                              <FileText size={20} className="text-red-500 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-700 truncate">{f.filename}</span>
                            </a>
                            {isAdmin && (
                              <button
                                onClick={() => setDeleteTarget({ filename: f.filename, type: 'ficha' })}
                                className="ml-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ─── Lightbox ───────────────────────────────────────────────────────── */}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}

      {/* ─── Delete confirm ─────────────────────────────────────────────────── */}
      {deleteTarget && (
        <DeleteModal
          filename={deleteTarget.filename}
          type={deleteTarget.type}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default CatalogoProductos;
